import { AppHubCard } from "@/components/common/AppHubCard";
import { AppHubPage } from "@/components/common/AppHubPage";
import { createFileRoute } from "@tanstack/react-router";
import {
    BookOpen,
    Calendar,
    Clipboard,
    FileText,
    Hammer,
    ListChecks,
    Mail,
    Syringe,
    Waves,
    MapPin,
    ShieldCheck,
    Tag,
} from "lucide-react";
import { useNav } from "../../../utils/nav";

const builderItems = [
    {
        title: "Forms",
        description: "Build forms that collect patient data. Forms define what information is tracked in diaries.",
        icon: <Clipboard className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-blue-500 to-blue-600",
        bgGradient: "bg-gradient-to-br from-blue-50 to-blue-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-blue-100/50 before:to-blue-200/50",
        borderColor: "border-blue-200 hover:border-blue-300",
        primaryButtonClassName: "bg-blue-600 hover:bg-blue-700",
        links: [{ to: "/builder/forms", label: "Open Forms" }],
    },
    {
        title: "Schedules",
        description:
            "Create schedules that define when forms should be completed. Set up daily, weekly, or custom timing.",
        icon: <Calendar className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-green-500 to-green-600",
        bgGradient: "bg-gradient-to-br from-green-50 to-green-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-green-100/50 before:to-green-200/50",
        borderColor: "border-green-200 hover:border-green-300",
        primaryButtonClassName: "bg-green-600 hover:bg-green-700",
        links: [{ to: "/builder/schedules", label: "Open Schedules" }],
    },
    {
        title: "Templates",
        description:
            "Manage diary templates that combine forms and schedules. Make templates public for all clinicians.",
        icon: <FileText className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-amber-500 to-amber-600",
        bgGradient: "bg-gradient-to-br from-amber-50 to-amber-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-amber-100/50 before:to-amber-200/50",
        borderColor: "border-amber-200 hover:border-amber-300",
        primaryButtonClassName: "bg-amber-600 hover:bg-amber-700",
        links: [{ to: "/builder/templates", label: "Open Templates" }],
    },
    {
        title: "Consents",
        description:
            "Author reusable consent content with a formattable editor. Attach consents to templates so patients sign them on their diary.",
        icon: <ShieldCheck className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-600",
        bgGradient: "bg-gradient-to-br from-indigo-50 to-indigo-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-indigo-100/50 before:to-indigo-200/50",
        borderColor: "border-indigo-200 hover:border-indigo-300",
        primaryButtonClassName: "bg-indigo-600 hover:bg-indigo-700",
        links: [{ to: "/builder/consents", label: "Open Consents" }],
    },
    {
        title: "Dictionaries",
        description:
            "Shared answer lists, such as medications or body areas. Form questions pick one so every form stays in step.",
        icon: <BookOpen className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-cyan-500 to-cyan-600",
        bgGradient: "bg-gradient-to-br from-cyan-50 to-cyan-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-cyan-100/50 before:to-cyan-200/50",
        borderColor: "border-cyan-200 hover:border-cyan-300",
        primaryButtonClassName: "bg-cyan-600 hover:bg-cyan-700",
        links: [{ to: "/builder/dictionaries", label: "Open Dictionaries" }],
    },
    {
        title: "Options",
        description: "Search every answer option across dictionaries and form questions, and see where each one lives.",
        icon: <ListChecks className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-lime-500 to-lime-600",
        bgGradient: "bg-gradient-to-br from-lime-50 to-lime-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-lime-100/50 before:to-lime-200/50",
        borderColor: "border-lime-200 hover:border-lime-300",
        primaryButtonClassName: "bg-lime-600 hover:bg-lime-700",
        links: [{ to: "/builder/options", label: "Open Options" }],
    },
    {
        title: "Surgeries",
        description:
            "Manage surgery options available in diaries. Publish options to make them visible to all clinicians.",
        icon: <Hammer className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-rose-500 to-rose-600",
        bgGradient: "bg-gradient-to-br from-rose-50 to-rose-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-rose-100/50 before:to-rose-200/50",
        borderColor: "border-rose-200 hover:border-rose-300",
        primaryButtonClassName: "bg-rose-600 hover:bg-rose-700",
        links: [{ to: "/builder/surgeries", label: "Open Surgeries" }],
    },
    {
        title: "Injection contents",
        description:
            "Manage injection substances and their dose/unit options. Make them public to offer them to all clinicians.",
        icon: <Syringe className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-violet-500 to-violet-600",
        bgGradient: "bg-gradient-to-br from-violet-50 to-violet-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-violet-100/50 before:to-violet-200/50",
        borderColor: "border-violet-200 hover:border-violet-300",
        primaryButtonClassName: "bg-violet-600 hover:bg-violet-700",
        links: [{ to: "/builder/injection-contents", label: "Open Injection contents" }],
    },
    {
        title: "Shockwave contents",
        description:
            "Manage shockwave type/impulses/dose/unit options available in diaries. Make them public to offer them to all clinicians.",
        icon: <Waves className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-fuchsia-500 to-fuchsia-600",
        bgGradient: "bg-gradient-to-br from-fuchsia-50 to-fuchsia-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-fuchsia-100/50 before:to-fuchsia-200/50",
        borderColor: "border-fuchsia-200 hover:border-fuchsia-300",
        primaryButtonClassName: "bg-fuchsia-600 hover:bg-fuchsia-700",
        links: [{ to: "/builder/shockwave-contents", label: "Open Shockwave contents" }],
    },
    {
        title: "Injection locations",
        description:
            "Manage anatomic area/site/subsite options for injections. Private locations are only offered to the clinician who created them.",
        icon: <MapPin className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-teal-500 to-teal-600",
        bgGradient: "bg-gradient-to-br from-teal-50 to-teal-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-teal-100/50 before:to-teal-200/50",
        borderColor: "border-teal-200 hover:border-teal-300",
        primaryButtonClassName: "bg-teal-600 hover:bg-teal-700",
        links: [{ to: "/builder/injection-locations", label: "Open Injection locations" }],
    },
    {
        title: "Message builders",
        description: "Edit reminder sequences and export them as JSON, including the variables each message can use.",
        icon: <Mail className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-sky-500 to-sky-600",
        bgGradient: "bg-gradient-to-br from-sky-50 to-sky-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-sky-100/50 before:to-sky-200/50",
        borderColor: "border-sky-200 hover:border-sky-300",
        primaryButtonClassName: "bg-sky-600 hover:bg-sky-700",
        links: [{ to: "/builder/messages", label: "Open Message builders" }],
    },
    {
        title: "Labels",
        description: "Manage diary labels and resource links. Labels group diaries by study or trial.",
        icon: <Tag className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-cyan-500 to-cyan-600",
        bgGradient: "bg-gradient-to-br from-cyan-50 to-cyan-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-cyan-100/50 before:to-cyan-200/50",
        borderColor: "border-cyan-200 hover:border-cyan-300",
        primaryButtonClassName: "bg-cyan-600 hover:bg-cyan-700",
        links: [{ to: "/builder/labels", label: "Open Labels" }],
    },
];

const Builder = () => {
    useNav([
        {
            label: "Builder",
            route: { to: "/builder" },
        },
    ]);

    // Clinicians are redirected away by `/builder` beforeLoad; hub is admin-only for now.
    return (
        <AppHubPage
            title="Builder"
            description="Create and manage forms, schedules, and templates for patient diaries."
        >
            {builderItems.map((item) => (
                <AppHubCard key={item.title} {...item} />
            ))}
        </AppHubPage>
    );
};

export const Route = createFileRoute("/_app/builder/")({
    component: Builder,
});
